import React from 'react';

import { Close } from '@mui/icons-material';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { SparkScrollableGridContainer } from 'sharedComponents/UI';
import { useSparkBuilderStore } from 'stores';
import {
  useBoardNodesByBoardID,
  useRedoBoard,
  useUndoBoard,
} from 'hooks/Board';
import { NoteAddIcon, RedoIcon, UndoIcon } from 'components/SVG';
import SparkOutputBoardListViewContainer from './SparkOutputBoardListViewContainer';
import {
  BoardNodeShimmer,
  DUMMY_NODES,
} from 'components/SparkRuns/SparkRunningProgressScreen';
import { useDialog } from 'hooks/common';
import AddNodeModal from './AddNodeModal';

type CreateHeaderProps = {
  title: string;
  onClose: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onAdd: () => void;
};
const CreateHeader = ({
  title,
  onClose,
  onAdd,
  onRedo,
  onUndo,
}: CreateHeaderProps) => (
  <Box
    display='flex'
    alignItems='center'
    justifyContent='space-between'
    flexWrap='nowrap'
    width='100%'
  >
    <Typography>{title}</Typography>
    <Box display='flex' alignItems='center' flexWrap='nowrap' gap={1}>
      <IconButton
        aria-label='AddCircleRoundedIcon'
        size='small'
        onClick={onAdd}
      >
        <NoteAddIcon width='20px' height='20px' />
      </IconButton>
      <IconButton aria-label='UndoIcon' onClick={onUndo} size='small'>
        <UndoIcon width='20' />
      </IconButton>
      <IconButton aria-label='RedoIcon' onClick={onRedo} size='small'>
        <RedoIcon width='20' />
      </IconButton>
      <IconButton
        aria-label='close'
        onClick={onClose}
        size='small'
        sx={{
          color: '#000',
        }}
      >
        <Close />
      </IconButton>
    </Box>
  </Box>
);

type SparkOutputBoardContainerProps = {
  onClose: () => void;
};

function SparkOutputBoardContainer({
  onClose,
}: SparkOutputBoardContainerProps) {
  const joinedSpark = useSparkBuilderStore(
    React.useCallback((state) => state.joinedSpark, [])
  );
  const outputBoardID = joinedSpark?.attributes.outputBoardID;
  const { mutateAsync: redoBoard } = useRedoBoard(outputBoardID);
  const { mutateAsync: undoBoard } = useUndoBoard(outputBoardID);
  const { isLoading: isLoadingBoardNodes, boardNodes } =
    useBoardNodesByBoardID(outputBoardID);

  const { open, openDialog, closeDialog } = useDialog();

  return (
    <SparkScrollableGridContainer
      header={
        <CreateHeader
          title='Output'
          onClose={onClose}
          onUndo={undoBoard}
          onRedo={redoBoard}
          onAdd={openDialog}
        />
      }
      scrollableContainerStyle={{
        paddingX: 2,
        marginBottom: 2,
        justifyContent: 'center',
      }}
    >
      {isLoadingBoardNodes && (
        <Grid container gap={2}>
          {DUMMY_NODES.map((node) => (
            <BoardNodeShimmer title={node.attributes.title} key={node.id} />
          ))}
        </Grid>
      )}
      {!isLoadingBoardNodes && (
        <Grid container maxWidth='800px'>
          <SparkOutputBoardListViewContainer
            boardID={outputBoardID}
            boardNodes={boardNodes}
          />
        </Grid>
      )}
      <AddNodeModal open={open} onClose={closeDialog} boardID={outputBoardID} />
    </SparkScrollableGridContainer>
  );
}

export default SparkOutputBoardContainer;
